import { useCallback, useEffect, useState } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LayoutGrid, Plus, Trash2 } from "lucide-react";
import WallEditor from "@/components/videowall/WallEditor";
import { toast } from "sonner";

function NewWallForm({ businessId, onCreated }) {
  const [name, setName] = useState("");
  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const { data } = await apiAdmin.post("/video-walls", { business_id: businessId, name: name.trim(), cols: 2, rows: 2 });
      setName("");
      onCreated(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={create} className="flex gap-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ej. Pared principal" data-testid="wall-new-name-input" />
      <Button type="submit" size="icon" disabled={!name.trim()} aria-label="Crear grupo" data-testid="wall-create-btn"><Plus className="h-4 w-4" /></Button>
    </form>
  );
}

function WallList({ walls, activeId, onSelect, onDelete }) {
  return (
    <div className="space-y-1.5">
      {walls.map((w) => (
        <div
          key={w.id}
          onClick={() => onSelect(w.id)}
          className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors duration-200 ${
            activeId === w.id ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-800"
          }`}
          data-testid={`wall-list-item-${w.id}`}
        >
          <LayoutGrid className="h-4 w-4 shrink-0 opacity-70" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{w.name}</div>
            <div className={`text-[11px] ${activeId === w.id ? "text-white/60" : "text-slate-500"}`}>
              {w.cols}×{w.rows} · {w.slots.length} TV · {w.items.length} contenido
              {w.window?.active && <span className="ml-1.5 text-emerald-400 font-medium">● en vivo</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(w); }}
            className="opacity-0 group-hover:opacity-100 rounded p-1 hover:bg-red-500/20 hover:text-red-500 transition-opacity"
            aria-label="Borrar grupo"
            data-testid={`wall-delete-${w.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function VideoWallPage() {
  const { activeBusiness } = useBusiness();
  const [walls, setWalls] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [media, setMedia] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    const bid = activeBusiness.id;
    const [w, t, m] = await Promise.all([
      apiAdmin.get(`/video-walls?business_id=${bid}`),
      apiAdmin.get(`/tvs?business_id=${bid}`),
      apiAdmin.get(`/media?business_id=${bid}`),
    ]);
    setWalls(w.data);
    setTvs(t.data.filter((x) => !x.pending));
    setMedia(m.data);
  }, [activeBusiness]);

  useEffect(() => { load(); }, [load]);

  const processing = walls.some((w) => w.items.some((i) => i.status === "processing"));
  useEffect(() => {
    if (!activeBusiness) return undefined;
    const id = setInterval(async () => {
      const { data } = await apiAdmin.get(`/video-walls?business_id=${activeBusiness.id}`);
      setWalls(data);
    }, processing ? 4000 : 15000);
    return () => clearInterval(id);
  }, [processing, activeBusiness]);

  const upsert = (w) => setWalls((list) => (list.some((x) => x.id === w.id) ? list.map((x) => (x.id === w.id ? w : x)) : [...list, w]));
  const remove = async (w) => {
    if (!window.confirm(`¿Borrar el grupo “${w.name}”? Las TVs vuelven a su menú normal.`)) return;
    try {
      await apiAdmin.delete(`/video-walls/${w.id}`);
      setWalls((list) => list.filter((x) => x.id !== w.id));
      if (activeId === w.id) setActiveId(null);
      toast.success("Grupo borrado");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const current = walls.find((w) => w.id === activeId) || walls[0];
  const takenElsewhere = new Set(walls.filter((w) => w.id !== current?.id).flatMap((w) => w.slots.map((s) => s.tv_id)));

  return (
    <div className="space-y-8" data-testid="video-wall-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Video Wall</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">Grupos de pantallas</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Acomoda varias TVs como una sola pantalla gigante. Cada TV sigue con su menú, y la imagen o video
          gigante sale al final de la lista o cada cierto tiempo, repartida entre todas.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 items-start">
        <aside className="rounded-xl border border-slate-200 bg-white p-4 space-y-4" data-testid="wall-sidebar">
          <NewWallForm businessId={activeBusiness?.id} onCreated={(w) => { upsert(w); setActiveId(w.id); }} />
          {walls.length === 0 ? (
            <p className="text-sm text-slate-400 px-1" data-testid="wall-list-empty">Crea tu primer grupo, ej. “Pared del mostrador”.</p>
          ) : (
            <WallList walls={walls} activeId={current?.id} onSelect={setActiveId} onDelete={remove} />
          )}
        </aside>
        {current ? (
          <WallEditor
            key={`${current.id}-${current.cols}x${current.rows}`}
            wall={current}
            tvs={tvs.filter((t) => !takenElsewhere.has(t.id))}
            media={media}
            onChanged={upsert}
          />
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 p-16 text-center text-slate-400">Selecciona o crea un grupo.</div>
        )}
      </div>
    </div>
  );
}
